import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import "./Footer.css";
import { useTProxy } from "@/i18nProxy";
import LegalLink from "@/modules/legal/components/LegalLink";

/** A footer column: plain on wide screens, a collapsible block on phones so the footer stays short. */
function FooterSection({ title, children, defaultOpen = false }) {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const apply = () => setCompact(media.matches);
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  if (!compact) {
    return (
      <div className="footer-section">
        <h4 className="footer-title">{title}</h4>
        <div className="footer-divider" />
        {children}
      </div>
    );
  }
  return (
    <div className={`footer-section footer-section--compact ${open ? "is-open" : ""}`}>
      <button type="button" className="footer-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="footer-title">{title}</span>
        <ChevronDown size={18} className="footer-toggle-icon" aria-hidden="true" />
      </button>
      {open && <div className="footer-section-body">{children}</div>}
    </div>
  );
}

const Footer = () => {
  const t = useTProxy();
  
  return (
    <footer className="custom-footer dark:bg-slate-800 dark:text-white">
      <div className="footer-top dark:bg-slate-800 dark:text-white">
        {/* Column 1: Tarh Links */}
        <FooterSection title={t.tarh || "Tarh"}>
          <ul className="footer-links ">
            <li>
              <a href="https://www.tarhplatform.com/" className="footer-link ">
                {t.homePage || "Home Page"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/about" className="footer-link">
                {t.aboutTarh || "About Tarh"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/who-we-are" className="footer-link">
                {t.whoWeAre || "Who We Are"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/services" className="footer-link">
                {t.electronicServices || "Electronic Services"}
              </a>
            </li>
            <li>
              <LegalLink type="privacy" className="footer-link">
                {t.privacyPolicy || "Privacy Policy"}
              </LegalLink>
            </li>
            <li>
              <LegalLink type="terms" className="footer-link">
                {t.termsAndConditions || "Terms & Conditions"}
              </LegalLink>
            </li>
          </ul>
        </FooterSection>

        {/* Column 2: Support */}
        <FooterSection title={t.supportAndAssistance || "Support and Assistance"}>
          <ul className="footer-links">
            <li>
              <a href="https://tarhplatform.com/contact" className="footer-link">
                {t.contactUs || "Contact us"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/helpcenter" className="footer-link">
                {t.guidanceLibrary || "Guidance Library"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/faq" className="footer-link">
                {t.faq || "Frequently Asked Questions"}
              </a>
            </li>
            <li>
              <a href="https://tarhplatform.com/guide" className="footer-link">
                {t.platformRegistrationSteps || "Platform Registration Steps"}
              </a>
            </li>
            {/* <li>
              <a href="https://tarhplatform.com/subscribe" className="footer-link">
                {t.subscription || "Subscription"}
              </a>
            </li> */}
          </ul>
        </FooterSection>

        {/* Column 3: Email */}
        <div className="footer-section footer-section--contact">
          <h4 className="footer-title">{t.email || "Email"}</h4>
          <div className="footer-divider" />
          <p>
            <a href="mailto:info@tarhplatform.com" className="footer-link">
              info@tarhplatform.com
            </a>
          </p>
        </div>

        {/* Column 4: Social + Apps */}
        <div className="footer-section footer-section--social">
          <h4 className="footer-title">{t.followUsOn || "Follow us on"}</h4>
          <div className="footer-divider" />
          <ul className="footer-social">
            <li>
              <a href="https://www.tarhplatform.com/" aria-label="YouTube">
                <img
                  src="https://static.wixstatic.com/media/11062b_f67ec9c36d8a431eabf904a991d9647f~mv2.png"
                  alt="YouTube"
                  className="footer-icon"
                />
              </a>
            </li>
            <li>
              <a href="https://www.tarhplatform.com/" aria-label="X">
                <img
                  src="https://static.wixstatic.com/media/11062b_17de014191ff44cab04a7676fe612204~mv2.png"
                  alt="X"
                  className="footer-icon"
                />
              </a>
            </li>
            <li>
              <a href="https://www.linkedin.com/company/%D9%85%D9%86%D8%B5%D8%A9-%D8%B7%D8%B1%D8%AD-%D9%84%D9%85%D9%86%D8%A7%D9%81%D8%B3%D8%A7%D8%AA-%D8%A7%D9%84%D9%82%D8%B7%D8%A7%D8%B9-%D8%A7%D9%84%D8%AE%D8%A7%D8%B5/" aria-label="LinkedIn">
                <img
                  src="https://static.wixstatic.com/media/11062b_a06d85fa94d64ef68680321f5a043a8c~mv2.png"
                  alt="LinkedIn"
                  className="footer-icon"
                />
              </a>
            </li>
          </ul>

          {/* App download */}
          <h4 className="footer-title mt-4">
            {t.downloadTarhApp || "Download the Tarh App"}{" "}
            <span className="footer-soon">({t.soon || "soon"})</span>
          </h4>
          <div className="footer-divider" />
          <div className="footer-apps">
            <img
              src="https://static.wixstatic.com/media/5f4314_e58d5abc73a84622ae327bb3dab640f3~mv2.png"
              alt="App Store / Google Play"
              className="footer-app-icon"
            />
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="footer-bottom">
        <p>
          {t.copyrightText ||
            "All rights reserved © 2025 – Tarh Platform – Kingdom of Saudi Arabia"}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
